import {
  Button,
  Col,
  Form,
  FormProps,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Tag,
  Space,
} from "antd";
import { FC, useEffect, useState } from "react";
import { useNotificationStore } from "../notifications.store";
import {
  TEMPLATE_KEYS,
  useCreateTemplate,
  useGetRules,
  useGetTemplate,
  useUpdateTemplate,
} from "~/services/template";
import { PreferredCommunicationMethod, TemplateType } from "~/utils/constants";
import { capitalize } from "~/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useNotification } from "~/hooks";

type Props = {};

const TEMPLATE_VARIABLES = [
  { label: 'Service #', value: '{{serial}}', color: 'blue', category: 'data' },
  { label: 'Service Date', value: '{{serviceDate}}', color: 'green', category: 'data' },
  { label: 'Location', value: '{{location}}', color: 'orange', category: 'data' },
  { label: 'Client Name', value: '{{clientName}}', color: 'purple', category: 'data' },
  { label: 'Staff Name', value: '{{staffName}}', color: 'cyan', category: 'data' },
  { label: 'Booking #', value: '{{bookingSerial}}', color: 'magenta', category: 'data' },
  { label: 'Staff Assignment Link', value: '{{staffAssignmentLink}}', color: 'red', category: 'link' },
  { label: 'Service Ticket Link', value: '{{serviceTicketLink}}', color: 'volcano', category: 'link' },
  { label: 'Booking Link', value: '{{bookingLink}}', color: 'gold', category: 'link' },
];

export const ModalTemplate: FC<Props> = () => {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const { success, error } = useNotification();
  const { filters, modalState, closeTemplateModal } = useNotificationStore();

  const { data: rules } = useGetRules();
  const { data: template, isLoading: loadingTemplate } = useGetTemplate(
    modalState.templateId
  );

  const mutationCreate = useCreateTemplate();
  const mutationUpdate = useUpdateTemplate();

  useEffect(() => {
    if (!template || !form) return;
    form.setFieldsValue({
      name: template.name,
      ruleId: template.rule?.id,
      type: template.type,
      channels: template.channels,
      subject: template.subject,
      body: template.body,
    });
  }, [form, template]);

  const handleSubmit: FormProps["onFinish"] = (values) => {
    const mutation = modalState.templateId ? mutationUpdate : mutationCreate;

    const payload = modalState.templateId
      ? { templateId: modalState.templateId, body: values }
      : values;

    mutation.mutate(payload, {
      onSuccess: () => {
        success({
          message: modalState.templateId
            ? "Template updated successfully"
            : "Template created successfully",
        });
        closeTemplateModal();
        qc.invalidateQueries({
          queryKey: TEMPLATE_KEYS.GET_TEMPLATES(filters),
        });
      },
      onError: (err) => {
        console.error(
          `Error ${modalState.templateId ? "updating" : "creating"} template:`,
          err
        );
        error({
          message: `Error ${
            modalState.templateId ? "updating" : "creating"
          } vessel`,
          description: err.message,
        });
      },
    });
  };

  const isLoading = mutationUpdate.isPending || mutationCreate.isPending;
  const modalTitle = template
    ? "Edit Template " + template.name
    : "Create New Template";

  // Function to insert variable at cursor position
  const insertVariable = (variable: string, fieldName: 'subject' | 'body') => {
    const currentValue = form.getFieldValue(fieldName) || '';
    const newValue = currentValue + variable;
    form.setFieldValue(fieldName, newValue);
  };

  return (
    <Modal
      destroyOnHidden
      open={modalState.openTemplate}
      onCancel={() => {
        closeTemplateModal();
        form.resetFields();
      }}
      title={
        <div className="py-2">
          <h2 className="text-xl font-medium">{modalTitle}</h2>
        </div>
      }
      width={{
        xs: "90%",
        sm: "80%",
        md: "70%",
        lg: "70%",
        xl: "50%",
        xxl: "50%",
      }}
      footer={null}
      loading={loadingTemplate}
    >
      <Form layout="vertical" form={form} onFinish={handleSubmit}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: "Name is required" }]}
            >
              <Input placeholder="Enter template name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Rule (Optional)"
              name="ruleId"
              help="You can assign this template to a rule later"
            >
              <Select
                placeholder="Select notification rule or leave blank"
                allowClear
                options={rules?.items.map((rule) => ({
                  label: rule.name,
                  value: rule.id,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Type"
              name="type"
              initialValue={TemplateType.CLIENT}
            >
              <Select
                placeholder="Select template type"
                options={Object.values(TemplateType).map((type) => ({
                  label: capitalize(type),
                  value: type,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Channel"
              name="channels"
              initialValue={PreferredCommunicationMethod.EMAIL}
            >
              <Radio.Group
                options={Object.values(PreferredCommunicationMethod).map(
                  (channel) => ({
                    label: capitalize(channel),
                    value: channel,
                  })
                )}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded mb-2">
              <h3 className="font-medium text-sm mb-2">Available Variables</h3>
              <p className="text-xs text-gray-600 mb-2">Click any tag to insert into content</p>

              {/* Data Variables */}
              <div className="mb-3">
                <div className="text-xs font-medium text-gray-700 mb-1">Data:</div>
                <Space size={[8, 8]} wrap>
                  {TEMPLATE_VARIABLES.filter(v => v.category === 'data').map((variable) => (
                    <Tag
                      key={variable.value}
                      color={variable.color}
                      style={{ cursor: 'pointer', fontSize: '12px' }}
                      onClick={() => insertVariable(variable.value, 'body')}
                    >
                      {variable.label}: {variable.value}
                    </Tag>
                  ))}
                </Space>
              </div>

              {/* Link Variables */}
              <div>
                <div className="text-xs font-medium text-gray-700 mb-1">Links (use in HTML anchor tags):</div>
                <Space size={[8, 8]} wrap>
                  {TEMPLATE_VARIABLES.filter(v => v.category === 'link').map((variable) => (
                    <Tag
                      key={variable.value}
                      color={variable.color}
                      style={{ cursor: 'pointer', fontSize: '12px' }}
                      onClick={() => insertVariable(variable.value, 'body')}
                    >
                      {variable.label}: {variable.value}
                    </Tag>
                  ))}
                </Space>
              </div>

              {/* Example */}
              <div className="mt-2 p-2 bg-white rounded text-xs">
                <strong>Example:</strong> {'<a href="{{staffAssignmentLink}}">View Assignment</a>'}
              </div>
            </div>
          </Col>

          <Col span={24}>
            <Form.Item
              label="Subject"
              name="subject"
              rules={[{ required: true, message: "Subject is required" }]}
            >
              <Input placeholder="e.g., You've been assigned to service {{serial}}" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label="Content (HTML Supported)"
              name="body"
              help="Use the variables above, supports HTML formatting"
            >
              <Input.TextArea
                placeholder={`Example with link:\n\nHello {{staffName}},\n\nYou've been assigned to service {{serial}} on {{serviceDate}} at {{location}}.\n\n<a href="{{staffAssignmentLink}}" style="background: #1890ff; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; display: inline-block;">View Your Assignment</a>\n\nThank you!`}
                rows={8}
              />
            </Form.Item>
          </Col>
        </Row>
        <div className="flex justify-end mt-4">
          <Button>Cancel</Button>
          <Button
            type="primary"
            className="ml-2"
            htmlType="submit"
            loading={isLoading}
          >
            {modalState.templateId ? "Update" : "Create"} Template
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
